import { R, type Ramp, type Toy } from "./toy.ts";
import { INK, WHITE, eyeBig, eyeDot, eyeGlow, eyeSleepy, nose, paws, smile, teeth } from "./parts.ts";
import type { Paint } from "./frontier.ts";
import type { SIDECHAIN_SPECIES_IDS } from "../data/speciesSidechain.ts";

/** Sidechain species ids. */
export type SidechainId = (typeof SIDECHAIN_SPECIES_IDS)[number];

/**
 * Sidechain bestiary: Halving Harvest, Ghostchain, Pump Jungle, Sakura Stake and the Rugpull
 * Ruins, on the same vinyl-toy rig and 32×32 design grid as creatures.ts (feet on row 29, key
 * light upper left). Original IP only.
 */

// ------------------------------------------------------------------------------------------------
// Small parts
// ------------------------------------------------------------------------------------------------

/** A little gold coin with a stamped centre. */
function coin(t: Toy, cx: number, cy: number, r = 1.8): void {
  t.ell(cx, cy, r, r, R.gold, { rim: false });
  t.px(Math.round(cx), Math.round(cy), R.gold, 2);
}

/** Carved, candle-lit triangle eye (pumpkins, lanterns). */
function carvedEye(t: Toy, x: number, y: number, dir: 1 | -1): void {
  t.poly([[x, y + 3], [x + 3.5, y + 3], [x + (dir > 0 ? 3 : 0.5), y]], R.ember, { tone: 1 });
  t.px(Math.round(x + 1.5), y + 2, R.ember, 0);
}

/** A leaf blade from (x0, y0) toward (x1, y1). */
function leaf(t: Toy, x0: number, y0: number, x1: number, y1: number, w: number, ramp: Ramp = R.green): void {
  const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
  const nx = -(y1 - y0), ny = x1 - x0, len = Math.hypot(nx, ny) || 1;
  t.poly([[x0, y0], [mx + (nx / len) * w, my + (ny / len) * w], [x1, y1], [mx - (nx / len) * w, my - (ny / len) * w]], ramp, { grad: true });
  t.line(x0, y0, x1, y1, ramp, 3);
}

/** Wavy ghost hem along the bottom of a spectral body. */
function hem(t: Toy, x0: number, x1: number, y: number, ramp: Ramp): void {
  for (let x = x0; x <= x1; x += 3) t.ell(x + 1, y, 1.8, 2.2, ramp, { spec: false, rim: false });
}

/** Tombstone slab with a rounded top. */
function slabStone(t: Toy, cx: number, top: number, bottom: number, half: number, ramp: Ramp): void {
  t.slab(cx, (top + bottom) / 2 + 1, half, (bottom - top) / 2, half * 0.9, ramp);
}

// ------------------------------------------------------------------------------------------------
// Halving Harvest
// ------------------------------------------------------------------------------------------------

const pumpkoin: Paint = (t, back) => {
  paws(t, 16, 5, 2.4, R.bark);
  // Three lobes read as a ribbed pumpkin.
  t.ell(10, 20, 5.5, 7.5, R.koi);
  t.ell(22, 20, 5.5, 7.5, R.koi);
  t.ell(16, 20, 7, 8.2, R.koi);
  t.cap(16, 12.5, 17, 7.5, 1.3, R.moss, { shade: 1 });
  leaf(t, 17, 9, 24, 5, 2.2, R.green);
  coin(t, 25, 4.5, 1.5);
  if (back) {
    t.vline(16, 14, 12, R.koi, 3);
    return;
  }
  carvedEye(t, 10.5, 15, 1);
  carvedEye(t, 18, 15, -1);
  t.rect(11, 22, 10, 2, R.ember, 1);
  for (const x of [12, 15, 18]) t.px(x, 22, R.koi, 3);
  t.px(13, 24, R.ember, 1); t.px(17, 24, R.ember, 1);
};

const gourdian: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Leaf cape behind the body.
  t.poly([[8, 14], [24, 14], [27, 27], [5, 27]], R.moss, { grad: true });
  for (const x of [11, 21]) t.cap(x, 22, x, 28, 2.3, R.bark, { shade: 1 });
  t.ell(16, 21, 7.5, 5.5, R.moss);
  // Vine arms; one holds a lantern gourd.
  t.cap(9, 18, m(4), 23, 1.6, R.moss, { shade: 1 });
  t.cap(23, 18, m(27), 22, 1.6, R.moss, { shade: 1 });
  t.vline(m(27), 22, 2, R.bark, 2);
  t.ell(m(27), 26, 2.4, 2.6, R.ember);
  t.px(m(27), 26, R.ember, 0);
  // The great pumpkin head.
  t.ell(9.5, 10.5, 6, 7, R.koi);
  t.ell(22.5, 10.5, 6, 7, R.koi);
  t.ell(16, 10.5, 7.5, 8, R.koi);
  t.cap(16, 3.5, 17.5, 0.5, 1.3, R.bark);
  leaf(t, 17, 3, 22, 1, 1.6, R.green);
  if (back) {
    t.vline(16, 4, 13, R.koi, 3);
    t.vline(10, 5, 11, R.koi, 3);
    t.vline(22, 5, 11, R.koi, 3);
    return;
  }
  // Stern carved brows and a jagged grin, candle-lit.
  t.poly([[9, 10], [14, 10], [14, 6.5]], R.ember, { tone: 1 });
  t.poly([[18, 10], [23, 10], [18, 6.5]], R.ember, { tone: 1 });
  t.rect(10, 13, 12, 3, R.ember, 1);
  for (const x of [11, 14, 17, 20]) t.px(x, 13, R.koi, 3);
  for (const x of [12, 15, 18]) t.px(x, 15, R.koi, 3);
  t.px(12, 8, R.ember, 0); t.px(19, 8, R.ember, 0);
};

const crowlet: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.poly([[m(20), 22], [m(28), 27], [m(25), 19]], R.crow, { grad: true });
  for (const x of [12, 18]) {
    t.vline(x, 26, 3, R.gold, 2);
    t.hline(x - 1, 29, 3, R.gold, 2);
  }
  t.ell(15.5, 21, 7.5, 6.5, R.crow);
  t.ell(8.5, 21, 3, 4.5, R.crow, { shade: 1 });
  t.ell(22.5, 21, 3, 4.5, R.crow, { shade: 1 });
  t.ell(15.5, 12, 6.8, 6, R.crow);
  t.poly([[14, 7], [15, 2], [17.5, 7]], R.crow, { grad: true });
  if (back) {
    t.hline(12, 20, 7, R.crow, 3);
    return;
  }
  t.ell(15.5, 23, 4, 3.4, R.wisp, { dome: 0.5, rim: false, spec: false });
  eyeBig(t, 10, 9, R.gold);
  eyeBig(t, 17, 9, R.gold);
  t.poly([[13.5, 14], [18.5, 14], [16, 18]], R.gold, { grad: true });
  coin(t, 19.5, 17.5, 1.6);
};

const crowllateral: Paint = (t, back) => {
  // Wings spread wide, primaries fanned.
  for (const s of [-1, 1] as const) {
    const x0 = 16 + s * 5;
    t.poly([[x0, 12], [16 + s * 15, 7], [16 + s * 15.5, 13], [16 + s * 13, 20], [x0, 22]], R.crow, { grad: true });
    for (let k = 0; k < 3; k++) t.line(16 + s * (9 + k * 2), 12 + k, 16 + s * (14 + k * 0.5), 9 + k * 3, R.crow, 3);
  }
  t.poly([[13, 24], [19, 24], [16, 31]], R.crow, { grad: true });
  for (const x of [13, 19]) {
    t.vline(x, 25, 3, R.gold, 2);
    t.hline(x - 1, 28, 3, R.gold, 1);
  }
  t.ell(16, 18.5, 6.5, 7.5, R.crow);
  t.ell(16, 8.5, 5.5, 5, R.crow);
  t.poly([[15, 4], [17, 0], [18, 4.5]], R.crow, { grad: true });
  if (back) return;
  eyeGlow(t, 11, 6, R.gold);
  eyeGlow(t, 17, 6, R.gold);
  t.poly([[14, 11], [18.5, 11], [16, 15.5]], R.gold, { grad: true });
  t.px(15, 11, WHITE);
  // Collateral chain and pendant.
  for (let i = 0; i < 9; i++) t.px(12 + i, 15 + Math.round(Math.sin((i / 8) * Math.PI) * 2), R.gold, i % 2);
  coin(t, 16, 19, 2);
};

const nutling: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Huge curled tail.
  t.ell(m(24), 15, 5.5, 9, R.brown);
  t.ell(m(25), 7.5, 4, 3.5, R.brown, { shade: -1 });
  t.px(m(26), 5, R.ice, 0); t.px(m(24), 6, R.ice, 1);
  paws(t, 13, 3.5, 2, R.brown);
  t.ell(13, 21.5, 6, 6, R.brown);
  t.ell(13, 12.5, 5.5, 5, R.brown);
  t.poly([[8.5, 9.5], [9, 4.5], [12, 8]], R.brown, { grad: true });
  t.poly([[17.5, 9.5], [17, 4.5], [14, 8]], R.brown, { grad: true });
  if (back) return;
  t.ell(13, 23, 3.8, 3.8, R.cream, { dome: 0.4, rim: false, spec: false });
  // The acorn it holds, with a frosty cap.
  t.ell(13, 20, 2.4, 2.8, R.bronze);
  t.ell(13, 17.8, 2.8, 1.4, R.ice, { spec: false });
  eyeBig(t, 8, 10, R.brown);
  eyeBig(t, 14, 10, R.brown);
  nose(t, 12, 14);
  t.px(9, 14, R.rose, 1); t.px(17, 14, R.rose, 1);
};

const nutvault: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.ell(m(26), 13, 5, 11, R.brown);
  t.ell(m(26), 3.5, 3.5, 2.8, R.ice, { shade: -1 });
  for (const x of [9, 20]) t.cap(x, 23, x, 28, 3, R.brown, { shade: 1, spec: false });
  t.ell(14.5, 19.5, 9, 8.5, R.brown);
  t.ell(14.5, 9, 6.5, 5.8, R.brown);
  t.poly([[9, 6], [9, 0.5], [12.5, 4]], R.brown, { grad: true });
  t.poly([[20, 6], [20, 0.5], [16.5, 4]], R.brown, { grad: true });
  if (back) {
    t.hline(9, 19, 11, R.brown, 3);
    return;
  }
  // Round vault door on its belly: bolts and a spoked wheel.
  t.ell(14.5, 20.5, 5.8, 5.8, R.steel);
  t.ring(14.5, 20.5, 5.8, 5.8, R.steel, 3);
  for (const [dx, dy] of [[0, -3], [3, 0], [0, 3], [-3, 0]] as const) t.line(14.5, 20.5, 14.5 + dx, 20.5 + dy, R.steel, 3);
  t.ell(14.5, 20.5, 1.4, 1.4, R.gold);
  // Bulging cheeks full of nuts.
  t.ell(9, 11.5, 2.8, 2.4, R.cream, { rim: false });
  t.ell(20, 11.5, 2.8, 2.4, R.cream, { rim: false });
  eyeBig(t, 10, 6, R.brown);
  eyeBig(t, 16, 6, R.brown);
  nose(t, 14, 11);
  t.px(13, 13, INK, 1); t.px(16, 13, INK, 1);
};

// ------------------------------------------------------------------------------------------------
// Ghostchain
// ------------------------------------------------------------------------------------------------

const rektling: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Trailing wisp tail.
  t.cap(m(20), 25, m(27), 28, 2, R.wisp, { spec: false });
  t.ell(15.5, 16, 8.5, 9, R.wisp, { dome: 0.7 });
  hem(t, 8, 22, 25, R.wisp);
  t.cap(8, 17, 4, 13, 1.5, R.wisp, { shade: 1 });
  if (back) return;
  // A broken coin it still clutches.
  t.ell(23.5, 19, 2.2, 2.2, R.gold);
  t.line(22.5, 17.5, 24.5, 20.5, INK, 1);
  // Hollow, droopy eyes and a wobbly mouth; a red chart runs down its side.
  t.ell(12, 13, 1.8, 2.4, R.ink, { flat: 1, occ: false });
  t.ell(19, 13, 1.8, 2.4, R.ink, { flat: 1, occ: false });
  t.px(12, 12, WHITE); t.px(19, 12, WHITE);
  t.hline(14, 18, 3, INK, 1); t.px(13, 19, INK, 1); t.px(17, 19, INK, 1);
  t.line(9, 8, 11, 10, R.red, 1); t.line(11, 10, 12, 9, R.red, 1); t.line(12, 9, 15, 7, R.red, 1);
};

const rektoplasm: Paint = (t, back) => {
  // Rising ecto columns behind.
  for (const [x, h] of [[6, 10], [26, 12], [10, 6]] as const) t.cap(x, 26, x, 26 - h, 2.2, R.jade, { spec: false });
  t.ell(16, 15, 11, 10, R.wisp, { dome: 0.6 });
  // Drips.
  for (const x of [8, 12, 17, 22]) t.cap(x, 22, x + 0.5, 28 - (x % 3), 1.6, R.wisp, { spec: false, rim: false });
  // Waving arms.
  t.cap(6, 14, 1.5, 9, 2, R.wisp, { shade: 1 });
  t.cap(26, 14, 30.5, 8, 2, R.wisp, { shade: 1 });
  if (back) {
    for (const x of [11, 16, 21]) t.ell(x, 12, 1.2, 1.2, R.jade, { spec: false, rim: false });
    return;
  }
  // Glowing green eyes and a wide, wobbling grin.
  eyeGlow(t, 9, 10, R.jade);
  eyeGlow(t, 18, 10, R.jade);
  t.rect(11, 17, 10, 3, INK, 1);
  t.rect(12, 18, 8, 1, R.jade, 1);
  for (const x of [12, 15, 18]) t.px(x, 17, WHITE);
  // Share arrows floating off it (it went viral).
  t.line(26, 3, 29, 1, R.lime, 1); t.px(28, 0, R.lime, 0); t.px(29, 2, R.lime, 0);
};

const tombstonk: Paint = (t, back) => {
  for (const x of [11, 20]) t.ell(x, 27.8, 2.8, 1.6, R.stone, { spec: false });
  slabStone(t, 15.5, 5, 27, 8.5, R.stone);
  // Moss on the crown.
  t.ell(12, 6.5, 3.5, 1.8, R.moss, { spec: false });
  t.ell(18, 6, 2.5, 1.4, R.moss, { spec: false });
  if (back) {
    t.hline(9, 18, 14, R.stone, 3);
    return;
  }
  // Carved rising arrow (it only goes up) above a sleepy face.
  t.line(11, 15, 15, 11, R.stone, 4); t.line(15, 11, 17, 13, R.stone, 4); t.line(17, 13, 20, 9, R.stone, 4);
  t.px(20, 9, R.lime, 1); t.px(19, 9, R.stone, 4); t.px(20, 10, R.stone, 4);
  eyeSleepy(t, 10, 18);
  eyeSleepy(t, 18, 18);
  smile(t, 14, 22, 4);
};

const cryptkeep: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  for (const x of [9, 22]) t.cap(x, 23, x, 28.5, 3.2, R.stone, { shade: 1, spec: false });
  // Stone arms; one holds a ring of keys.
  t.cap(6, 13, m(3), 22, 2.6, R.stone, { shade: 1 });
  t.cap(26, 13, m(28), 21, 2.6, R.stone, { shade: 1 });
  t.ring(m(28), 24, 2.4, 2.4, R.gold, 1);
  t.px(m(29), 27, R.gold, 1); t.px(m(27), 27, R.gold, 0);
  t.slab(16, 17, 10, 10, 3, R.stone);
  // Crypt roof hat.
  t.poly([[4, 8], [16, 1], [28, 8]], R.slate, { grad: true });
  t.hline(4, 8, 25, R.slate, 2);
  if (back) {
    for (const y of [12, 17, 22]) t.hline(7, y, 18, R.stone, 3);
    return;
  }
  // Iron crypt door with a keyhole, lantern eyes.
  t.slab(16, 20, 5, 6, 3, R.steel, { dome: 0.3 });
  t.ell(16, 19, 1.2, 1.2, INK, { flat: 1, occ: false });
  t.vline(16, 20, 3, INK, 1);
  eyeGlow(t, 9, 10, R.ember);
  eyeGlow(t, 19, 10, R.ember);
  t.ell(9, 7.5, 1.6, 1, R.moss, { spec: false });
};

const webthree: Paint = (t, back) => {
  // Eight thin legs.
  for (const s of [-1, 1] as const) {
    for (let k = 0; k < 4; k++) {
      const y0 = 17 + k * 2;
      const kx = 16 + s * (8 + k), ky = y0 - 5 + k * 2;
      t.line(16 + s * 4, y0, kx, ky, R.steel, 2);
      t.line(kx, ky, 16 + s * (10 + k * 1.5), 28, R.steel, 3);
    }
  }
  t.ell(16, 15, 8, 7, R.chrome);
  t.ell(16, 22, 5, 3.6, R.chrome, { shade: 1 });
  if (back) {
    // Node network pattern on the abdomen.
    for (const [x, y] of [[12, 12], [16, 10], [20, 12], [14, 17], [18, 17]] as const) t.ell(x, y, 1.2, 1.2, R.aqua, { spec: false, rim: false });
    t.line(12, 12, 16, 10, R.aqua, 2); t.line(16, 10, 20, 12, R.aqua, 2); t.line(12, 12, 14, 17, R.aqua, 2); t.line(20, 12, 18, 17, R.aqua, 2);
    return;
  }
  // Three glowing eyes and small fangs.
  eyeGlow(t, 9, 11, R.aqua);
  eyeGlow(t, 19, 11, R.aqua);
  t.ell(16, 9, 1.5, 1.5, R.aqua, { spec: false });
  t.px(15, 8, WHITE);
  t.px(14, 18, WHITE); t.px(17, 18, WHITE);
  t.hline(14, 17, 4, INK, 1);
};

const arachnft: Paint = (t, back) => {
  // Eight golden legs, each tipped with a different gem.
  const gems: Ramp[] = [R.aqua, R.rose, R.lime, R.violet];
  for (const s of [-1, 1] as const) {
    for (let k = 0; k < 4; k++) {
      const y0 = 15 + k * 2;
      const kx = 16 + s * (9 + k * 1.5), ky = y0 - 7 + k * 2;
      t.line(16 + s * 5, y0, kx, ky, R.gold, 2);
      const fx = 16 + s * (11 + k * 1.3), fy = 28;
      t.line(kx, ky, fx, fy, R.gold, 2);
      t.px(Math.round(fx), fy, gems[k]!, 0);
    }
  }
  // Big abdomen framed like a one-of-one.
  t.ell(16, 20, 8.5, 6.5, R.crow);
  t.ell(16, 11, 6.5, 5.5, R.crow);
  if (back) {
    t.rect(11, 16, 10, 8, R.gold, 1);
    t.rect(12, 17, 8, 6, R.night, 2);
    t.ell(16, 20, 2, 2, R.moon, { spec: false, rim: false });
    return;
  }
  eyeGlow(t, 11, 8, R.neon);
  eyeGlow(t, 17, 8, R.neon);
  t.px(15, 7, R.neon, 1); t.px(16, 6, R.neon, 1);
  t.px(14, 14, WHITE); t.px(18, 14, WHITE);
  // Tiny crown and a gold frame on its belly.
  t.poly([[13, 6], [14, 3], [16, 5], [18, 3], [19, 6]], R.gold, { grad: true });
  t.rect(12, 18, 8, 5, R.gold, 1);
  t.rect(13, 19, 6, 3, R.night, 2);
  t.px(15, 20, R.moon, 0);
};

const deadcat: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Bent, bouncing tail.
  t.cap(m(21), 24, m(27), 18, 1.6, R.crow, { shade: 1 });
  t.cap(m(27), 18, m(25), 12, 1.6, R.crow, { shade: 1 });
  paws(t, 15, 4, 2.2, R.crow);
  t.ell(15, 22, 7, 5.5, R.crow);
  t.ell(15, 13, 7, 6, R.crow);
  t.poly([[9, 10], [8.5, 3.5], [13, 8]], R.crow, { grad: true });
  t.poly([[21, 10], [21.5, 3.5], [17, 8]], R.crow, { grad: true });
  // Stitches on one ear.
  t.px(m(10), 6, R.paper, 0); t.px(m(9), 7, R.paper, 0);
  if (back) return;
  // One eye crossed out, one wide open.
  t.line(9, 11, 12, 14, R.paper, 1); t.line(12, 11, 9, 14, R.paper, 1);
  eyeBig(t, 16, 10, R.lime);
  nose(t, 14, 15);
  smile(t, 13, 16, 4);
  // A plaster on its cheek.
  t.rect(18, 15, 3, 2, R.cream, 1);
  t.px(19, 15, R.cream, 3);
};

const catacomb: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Three ghost tails (of nine).
  for (const [x, y] of [[26, 8], [29, 14], [27, 21]] as const) {
    t.cap(m(21), 21, m(x), y, 1.4, R.wisp, { spec: false });
    t.ell(m(x), y, 1.8, 1.8, R.wisp, { spec: false, rim: false });
  }
  for (const x of [9, 20]) t.cap(x, 23, x, 28.5, 2.6, R.crow, { shade: 1, spec: false });
  t.ell(14.5, 20, 9, 7, R.crow);
  t.ell(14.5, 10, 7.5, 6.5, R.crow);
  t.poly([[8, 7], [7, -0.5], [12.5, 5]], R.crow, { grad: true });
  t.poly([[21, 7], [22, -0.5], [16.5, 5]], R.crow, { grad: true });
  if (back) {
    for (let i = 0; i < 4; i++) t.hline(10, 16 + i * 2, 9, R.paper, 2);
    return;
  }
  // Ribcage markings and a bone collar.
  t.vline(14, 16, 8, R.paper, 1);
  for (let i = 0; i < 3; i++) { t.hline(10, 17 + i * 2.5, 9, R.paper, 2); }
  t.hline(9, 15, 12, R.paper, 1);
  t.ell(9, 15, 1.2, 1.2, R.paper, { spec: false }); t.ell(20, 15, 1.2, 1.2, R.paper, { spec: false });
  eyeGlow(t, 9, 8, R.jade);
  eyeGlow(t, 16, 8, R.jade);
  nose(t, 13, 12);
  teeth(t, 11, 13, 7);
};

// ------------------------------------------------------------------------------------------------
// Pump Jungle
// ------------------------------------------------------------------------------------------------

const toucoin: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(3, 28.5, 29, 28.5, 1.3, R.bark, { spec: false });
  t.cap(m(13), 22, m(9), 29, 2, R.crow, { shade: 1 });
  for (const x of [13, 17]) t.hline(x - 1, 27, 3, R.gold, 1);
  t.ell(15, 19, 6.5, 7.5, R.crow);
  t.ell(15, 9.5, 5.5, 5, R.crow);
  if (back) {
    t.ell(15, 18, 4, 5, R.crow, { shade: 1 });
    return;
  }
  t.ell(14, 14.5, 3.6, 3.4, R.cream, { rim: false, spec: false });
  // The famous beak: orange on top, gold underneath, red tip, a coin on its side.
  t.poly([[18, 6.5], [29.5, 9.5], [28, 12], [18, 10.5]], R.koi, { grad: true });
  t.poly([[18, 10.5], [28, 12], [26.5, 13.5], [18, 12.5]], R.gold, { grad: true });
  t.px(29, 10, R.red, 1); t.px(28, 11, R.red, 1);
  coin(t, 23, 9, 1.3);
  t.ell(14, 8.5, 2.2, 2.2, R.blue, { spec: false });
  t.rect(13, 8, 2, 2, INK, 1);
  t.px(13, 8, WHITE);
};

const beakfolio: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  t.cap(2, 29, 30, 29, 1.4, R.bark, { spec: false });
  t.cap(m(12), 23, m(7), 30, 2.4, R.crow, { shade: 1 });
  // Half-open wings.
  t.poly([[9, 13], [2, 21], [8, 24], [11, 20]], R.crow, { grad: true });
  t.poly([[19, 13], [24, 22], [18, 24]], R.crow, { grad: true });
  for (const x of [11, 17]) t.hline(x - 1, 28, 3, R.gold, 1);
  t.ell(14, 19, 7, 8.5, R.crow);
  t.ell(14, 8.5, 6, 5.5, R.crow);
  // Leaf crest.
  leaf(t, 13, 4, 9, -0.5, 1.6, R.green);
  leaf(t, 15, 3.5, 17, -1, 1.4, R.lime);
  if (back) return;
  t.ell(13, 14.5, 4, 3.6, R.cream, { rim: false, spec: false });
  // A striped portfolio beak: every band a different winning position.
  t.poly([[18, 5], [31, 8.5], [29.5, 11.5], [18, 10]], R.koi, { grad: true });
  t.poly([[18, 10], [29.5, 11.5], [27.5, 13.5], [18, 12.5]], R.gold, { grad: true });
  for (const [x, ramp] of [[21, R.lime], [24, R.blue], [27, R.rose]] as const) t.vline(x, 6 + Math.round((x - 18) * 0.25), 6, ramp, 1);
  t.px(30, 9, R.red, 1);
  t.ell(13, 8, 2.4, 2.4, R.blue, { spec: false });
  t.rect(12, 7, 2, 2, INK, 1);
  t.px(12, 7, WHITE);
  // Holding a fresh leaf in one foot.
  leaf(t, 18, 27, 23, 23, 1.5, R.green);
};

const vinesnek: Paint = (t, back) => {
  // Three stacked coils.
  t.ell(16, 25.5, 10, 3.6, R.green);
  t.ell(15, 20.5, 8, 3.4, R.green);
  t.ell(16, 16, 6, 3, R.green);
  if (!back) for (const [x, y] of [[9, 25], [20, 26], [12, 20], [19, 21]] as const) leaf(t, x, y, x + 2, y - 1.5, 0.9, R.lime);
  // Neck and head.
  t.cap(17, 15, 17, 9, 2.3, R.green);
  t.ell(17, 7.5, 5, 4, R.green);
  leaf(t, 18, 4, 23, 0.5, 1.6, R.lime);
  if (back) {
    for (const x of [10, 16, 22]) t.ell(x, 24.5, 1.2, 1, R.moss, { spec: false, rim: false });
    return;
  }
  t.ell(16, 21.5, 3, 1.4, R.lime, { spec: false, rim: false });
  eyeSleepy(t, 13, 6);
  eyeSleepy(t, 18, 6);
  // Smug grin and a forked tongue.
  t.hline(14, 10, 6, INK, 1);
  t.px(20, 9, INK, 1);
  t.vline(16, 11, 2, R.rose, 1);
  t.px(15, 13, R.rose, 1); t.px(17, 13, R.rose, 1);
};

const vineconda: Paint = (t, back) => {
  // Massive coils with rug diamonds woven through.
  t.ell(16, 26, 14.5, 4, R.moss);
  t.ell(15, 20.5, 12, 4, R.moss);
  t.ell(17, 15, 9, 3.6, R.moss);
  for (const [x, y] of [[6, 26], [13, 27], [21, 26], [27, 25], [9, 21], [17, 21], [23, 20], [13, 15], [21, 15]] as const) {
    t.poly([[x, y - 1.6], [x + 1.6, y], [x, y + 1.6], [x - 1.6, y]], R.violet, { tone: back ? 2 : 1 });
  }
  // Hooded head with a flower crown.
  t.cap(17, 14, 17, 8, 3, R.moss);
  t.poly([[10, 11], [17, 2], [24, 11], [17, 13]], R.green, { grad: true });
  t.ell(17, 6.5, 5.5, 4.2, R.moss);
  for (const [x, y, ramp] of [[12, 3, R.rose], [17, 1.5, R.gold], [22, 3, R.rose]] as const) {
    t.ell(x, y, 1.8, 1.6, ramp, { spec: false });
    t.px(x, y, R.gold, 0);
  }
  if (back) return;
  eyeGlow(t, 13, 5, R.gold);
  eyeGlow(t, 18, 5, R.gold);
  t.hline(14, 10, 6, INK, 1);
  t.px(14, 9, WHITE); t.px(19, 9, WHITE);
  t.vline(17, 11, 2, R.rose, 1);
};

const slothlet: Paint = (t, back) => {
  // Sitting, arms hanging with long hook claws.
  t.ell(16, 23, 8.5, 6, R.brown, { shade: 1 });
  for (const s of [-1, 1] as const) {
    t.cap(16 + s * 7, 17, 16 + s * 10, 25, 2, R.brown, { shade: 1 });
    for (let k = 0; k < 3; k++) t.px(16 + s * (9 + k), 27, R.cream, 3);
  }
  t.ell(16, 13, 7.5, 6.5, R.brown);
  // Sprout on its head.
  t.vline(16, 4, 3, R.green, 2);
  leaf(t, 16, 5, 12, 2.5, 1.2, R.lime);
  leaf(t, 16, 5, 20, 2.5, 1.2, R.green);
  if (back) return;
  t.ell(16, 14, 5.5, 4.5, R.cream, { dome: 0.4, rim: false, spec: false });
  // Dark eye bands and sleepy eyes.
  t.ell(12.5, 13, 2.2, 1.5, R.bark, { flat: 1, occ: false });
  t.ell(19.5, 13, 2.2, 1.5, R.bark, { flat: 1, occ: false });
  t.hline(11, 13, 3, INK, 1); t.hline(18, 13, 3, INK, 1);
  nose(t, 15, 15);
  smile(t, 14, 16, 4);
};

const slothstake: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Cross-legged bulk covered in moss.
  t.ell(16, 24, 12, 5.5, R.brown, { shade: 1 });
  t.ell(16, 18, 10, 8, R.brown);
  t.ell(16, 13, 11, 5, R.moss, { dome: 0.5 });
  for (const x of [7, 11, 16, 21, 25]) t.cap(x, 15, x + 0.5, 18 + (x % 3), 1.2, R.moss, { spec: false, rim: false });
  // Mushrooms and a flower sprouting from the moss.
  t.ell(m(8), 10.5, 2, 1.3, R.coral, { spec: false });
  t.vline(m(8), 11, 2, R.paper, 1);
  t.ell(m(25), 11, 1.6, 1.6, R.pink, { spec: false });
  t.px(m(25), 11, R.gold, 0);
  // Long arms resting on the knees.
  for (const s of [-1, 1] as const) {
    t.cap(16 + s * 9, 17, 16 + s * 7, 24, 2.4, R.brown, { shade: 1 });
    for (let k = 0; k < 3; k++) t.px(16 + s * (5 + k), 26, R.cream, 3);
  }
  t.ell(16, 8, 7, 5.5, R.brown);
  t.ell(16, 5, 6, 2.5, R.moss, { spec: false });
  if (back) return;
  t.ell(16, 9, 5, 4, R.cream, { dome: 0.4, rim: false, spec: false });
  t.ell(12.5, 8.5, 2.2, 1.4, R.bark, { flat: 1, occ: false });
  t.ell(19.5, 8.5, 2.2, 1.4, R.bark, { flat: 1, occ: false });
  t.hline(11, 8, 3, INK, 1); t.hline(18, 8, 3, INK, 1);
  nose(t, 15, 10);
  smile(t, 14, 11, 4);
  // A moss beard.
  t.poly([[13, 12.5], [19, 12.5], [16, 16]], R.moss, { grad: true });
};

// ------------------------------------------------------------------------------------------------
// Sakura Stake
// ------------------------------------------------------------------------------------------------

const koinfish: Paint = (t, back) => {
  // Splash at the base.
  for (const [x, y] of [[9, 28], [14, 29], [20, 28.5], [24, 27.5]] as const) t.ell(x, y, 2.2, 1.4, R.ice, { spec: false });
  // Arched leap: tail low-left, head high-right.
  t.cap(9, 22, 14, 16.5, 4.8, R.koi);
  t.cap(14, 16.5, 21, 12.5, 5.6, R.koi);
  t.ell(22.5, 11, 5.8, 5, R.koi);
  // Fan tail.
  t.poly([[9, 22], [2, 22], [5, 27], [9, 25]], R.koi, { grad: true });
  t.poly([[9, 22], [4, 17], [8, 19]], R.koi, { grad: true });
  // White patches and coin scales.
  t.ell(16, 13.5, 2.6, 2, R.paper, { spec: false, rim: false });
  t.ell(11, 19.5, 2, 1.6, R.paper, { spec: false, rim: false });
  if (!back) for (const [x, y] of [[13, 17], [16, 16], [19, 14], [11, 21]] as const) t.px(x, y, R.gold, 1);
  // Leaf fins.
  leaf(t, 15, 18, 13, 23, 1.2, R.green);
  leaf(t, 19, 9, 18, 5, 1.1, R.green);
  if (back) return;
  t.rect(23, 8, 3, 3, INK, 1);
  t.px(23, 8, WHITE);
  t.hline(26, 13, 2, INK, 1);
  t.px(28, 12, INK, 1);
  // Whiskers.
  t.line(26, 13, 29, 15, R.gold, 2);
};

const ryukoin: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Serpentine S body.
  t.cap(m(5), 27, m(12), 23, 3.6, R.koi);
  t.cap(m(12), 23, m(20), 22, 4.2, R.koi);
  t.cap(m(20), 22, m(24), 15, 4.4, R.koi);
  t.cap(m(24), 15, m(18), 10, 4.2, R.koi);
  // Tail fan.
  t.poly([[m(5), 27], [m(0.5), 24], [m(1.5), 30], [m(6), 29.5]], R.koi, { grad: true });
  // Belly scales and spine fins.
  if (!back) for (const [x, y] of [[10, 25], [15, 24.5], [20, 24], [23, 19], [22, 14]] as const) t.ell(m(x), y, 1.3, 1.1, R.ice, { spec: false, rim: false });
  for (const [x, y] of [[9, 20.5], [15, 19], [19, 17], [26, 12]] as const) t.poly([[m(x - 1), y + 1], [m(x), y - 2], [m(x + 1.5), y + 1]], R.aqua, { tone: 1 });
  // Head with crescent horns.
  t.ell(m(15), 8.5, 5.5, 4.5, R.koi);
  t.poly([[m(11), 5], [m(9), 0], [m(12.5), 3.5]], R.moon, { grad: true });
  t.poly([[m(18), 4.5], [m(21), 0], [m(18.5), 3]], R.moon, { grad: true });
  if (back) return;
  eyeGlow(t, 13, 6, R.aqua);
  t.hline(10, 11, 4, INK, 1);
  // Long whiskers trailing blossoms.
  t.line(11, 10, 5, 13, R.gold, 1);
  t.line(19, 10, 26, 6, R.gold, 1);
  t.ell(5, 14, 1.4, 1.4, R.pink, { spec: false });
  t.ell(27, 5.5, 1.4, 1.4, R.pink, { spec: false });
  t.px(16, 10, WHITE);
};

const origamint: Paint = (t, back) => {
  // A folded paper crane: flat facets, crisp creases.
  t.poly([[6, 22], [16, 16], [26, 22], [16, 26]], R.paper, { grad: true });
  t.poly([[16, 16], [5, 6], [12, 18]], R.paper, { tone: 0 });
  t.poly([[16, 16], [27, 5], [20, 18]], R.paper, { tone: 1 });
  // Neck and head fold.
  t.poly([[9, 20], [4, 11], [5.5, 10], [11, 19]], R.paper, { tone: 1 });
  t.poly([[4, 11], [1.5, 12.5], [5.5, 10]], R.paper, { tone: 2 });
  // Tail fold.
  t.poly([[23, 21], [29, 13], [27.5, 21]], R.paper, { tone: 2 });
  t.line(6, 22, 26, 22, R.paper, 3);
  // Receipt print on the wings.
  for (let i = 0; i < 3; i++) t.hline(19, 9 + i * 2.5, 4 - i, R.lime, 2);
  if (back) return;
  t.px(4, 11, INK, 1);
  t.px(5, 10, WHITE);
  coin(t, 16, 22, 1.4);
};

const craneprint: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Standing on one leg.
  t.vline(16, 22, 7, R.ink, 0); t.hline(14, 29, 5, R.ink, 0);
  // Body and raised wings printed like fresh bills.
  t.ell(16, 18, 7, 5, R.paper);
  for (const s of [-1, 1] as const) {
    t.poly([[16 + s * 3, 15], [16 + s * 14, 3], [16 + s * 12, 11], [16 + s * 7, 18]], R.paper, { grad: true });
    for (let k = 0; k < 3; k++) t.line(16 + s * (6 + k * 2), 14 - k, 16 + s * (9 + k * 2), 11 - k * 2, R.lime, 2);
    t.px(16 + s * 13, 4, R.ink, 1);
  }
  // Long neck and crested head with a crescent.
  t.cap(m(19), 16, m(22), 7, 1.6, R.paper);
  t.ell(m(22), 5.5, 2.8, 2.4, R.paper);
  t.poly([[m(24), 5], [m(30), 6.5], [m(24), 7]], R.gold, { grad: true });
  if (back) return;
  t.ell(22, 3.5, 1.6, 1.2, R.moon, { spec: false });
  t.px(23, 3, R.night, 2);
  t.rect(21, 5, 2, 2, INK, 1);
  t.px(21, 5, WHITE);
  coin(t, 14, 19, 1.5);
};

const sproutle: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  for (const x of [9, 22]) t.ell(x, 27, 2.6, 1.8, R.jade, { spec: false });
  t.ell(m(26.5), 22.5, 3.5, 3, R.jade);
  // Domed shell with hex plates.
  t.ell(15, 21, 10, 6.5, R.moss);
  for (const [x, y] of [[11, 19], [15, 17.5], [19, 19], [13, 22.5], [17, 22.5]] as const) t.ring(x, y, 1.8, 1.4, R.moss, 3);
  t.hline(5, 25, 21, R.sandstone, 2);
  // A sapling growing on top.
  t.vline(15, 9, 7, R.bark, 1);
  leaf(t, 15, 11, 10, 8, 1.8, R.lime);
  leaf(t, 15, 10, 20, 6, 1.8, R.green);
  leaf(t, 15, 9, 14, 4, 1.4, R.lime);
  if (back) return;
  eyeBig(t, 25, 20, R.jade);
  t.px(28, 24, INK, 1); t.px(27, 25, INK, 1);
};

const bonsaitoise: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  for (const x of [6, 13, 19, 25]) t.cap(x, 25, x, 28.5, 2.4, R.jade, { spec: false, shade: 1 });
  t.ell(m(28), 20, 3.8, 3.4, R.jade);
  // Stone shell like a garden rock.
  t.ell(15, 20, 12.5, 7, R.stone);
  for (const [x, y] of [[8, 19], [14, 17], [20, 18.5], [11, 23], [18, 23]] as const) t.ring(x, y, 2.2, 1.6, R.stone, 3);
  t.hline(3, 25, 25, R.moss, 2);
  // A pruned bonsai: twisted trunk and two blossom pads.
  t.cap(15, 14, 13, 9, 1.6, R.bark);
  t.cap(13, 9, 17, 5, 1.2, R.bark);
  t.ell(9, 8, 5, 2.8, R.pink, { rim: false });
  t.ell(19, 4.5, 5.5, 3, R.pink, { rim: false });
  t.ell(15, 6.5, 3, 2, R.green, { rim: false });
  if (back) return;
  eyeSleepy(t, 26, 18);
  t.hline(28, 22, 2, INK, 1);
};

// ------------------------------------------------------------------------------------------------
// Rugpull Ruins
// ------------------------------------------------------------------------------------------------

const golemint: Paint = (t, back) => {
  // Stacked temple blocks.
  for (const x of [10, 21]) t.slab(x, 26.5, 3.2, 2.5, 1, R.sandstone);
  t.slab(15.5, 18.5, 8.5, 6.5, 2, R.sandstone);
  t.slab(4.5, 18, 2.8, 4.5, 1, R.sandstone, { shade: 1 });
  t.slab(26.5, 18, 2.8, 4.5, 1, R.sandstone, { shade: 1 });
  t.slab(15.5, 8.5, 6, 4.5, 1.5, R.sandstone);
  // Moss on the shoulders and crown.
  t.ell(9, 12.5, 3, 1.4, R.moss, { spec: false });
  t.ell(20, 4.5, 3.5, 1.3, R.moss, { spec: false });
  if (back) {
    t.hline(8, 18, 16, R.sandstone, 3);
    t.vline(15, 13, 11, R.sandstone, 3);
    return;
  }
  // A minted coin set in its chest; one glowing slot eye.
  coin(t, 15.5, 18.5, 2.6);
  t.px(15, 18, R.gold, 3);
  t.rect(11, 7, 9, 2, INK, 1);
  t.rect(12, 7, 7, 1, R.aqua, 1);
  t.px(15, 7, WHITE);
  t.hline(13, 11, 5, R.sandstone, 3);
};

const colosstake: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  for (const x of [8, 23]) t.slab(x, 25.5, 4.2, 3.6, 1.2, R.sandstone, { shade: 1 });
  t.slab(15.5, 17, 11, 8, 2.5, R.sandstone);
  t.slab(2.5, 17, 2.5, 6, 1, R.sandstone, { shade: 1 });
  t.slab(28.5, 17, 2.5, 6, 1, R.sandstone, { shade: 1 });
  t.slab(15.5, 9, 5, 3.6, 1.2, R.sandstone);
  // A forest grew on its shoulders: two trees and a curtain of vines.
  for (const [x, top] of [[m(6), 1], [m(26), 3]] as const) {
    t.vline(x, top + 5, 5, R.bark, 1);
    t.ell(x, top + 3, 4, 3.4, R.green);
  }
  for (const x of [7, 12, 20, 25]) t.cap(x, 10, x + 0.5, 15 + (x % 4), 0.8, R.moss, { spec: false, rim: false });
  if (back) {
    for (const y of [13, 19]) t.hline(6, y, 20, R.sandstone, 3);
    return;
  }
  eyeGlow(t, 12, 8, R.aqua);
  eyeGlow(t, 16, 8, R.aqua);
  t.hline(13, 12, 5, R.sandstone, 4);
  // Carved sigil: a sprouting seed.
  t.ell(15.5, 19, 3.2, 3.2, R.sandstone, { flat: 3 });
  t.vline(15, 17, 4, R.lime, 1);
  t.px(14, 17, R.lime, 0); t.px(16, 16, R.lime, 0);
};

/** Striped royal headcloth (nemes-style), draped behind a face centred at cx. */
function headcloth(t: Toy, cx: number, top: number, half: number, drop: number, a: Ramp, b: Ramp): void {
  t.poly([[cx - half, top + 3], [cx, top - 1], [cx + half, top + 3], [cx + half + 1, top + drop], [cx - half - 1, top + drop]], a, { grad: true });
  for (let y = top + 3; y < top + drop; y += 2) {
    t.hline(Math.round(cx - half - 1), y, 3, b, 1);
    t.hline(Math.round(cx + half - 1), y, 3, b, 1);
  }
}

const riddlekit: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Question-mark tail.
  t.cap(m(21), 24, m(26), 20, 1.3, R.sandstone, { shade: 1 });
  t.cap(m(26), 20, m(26), 14, 1.3, R.sandstone, { shade: 1 });
  t.cap(m(26), 14, m(23), 12, 1.3, R.sandstone, { shade: 1 });
  t.ell(m(24), 26.5, 1.2, 1.2, R.sandstone, { spec: false });
  paws(t, 14, 4, 2.2, R.sandstone);
  t.ell(14, 22, 6.5, 5.5, R.sandstone);
  headcloth(t, 14, 6, 7, 11, R.gold, R.navy);
  t.ell(14, 12.5, 6, 5.2, R.sandstone);
  t.poly([[9, 8], [8.5, 2.5], [12, 6]], R.sandstone, { grad: true });
  t.poly([[19, 8], [19.5, 2.5], [16, 6]], R.sandstone, { grad: true });
  if (back) return;
  eyeBig(t, 9, 10, R.violet);
  eyeBig(t, 15, 10, R.violet);
  nose(t, 13, 15);
  t.px(12, 16, INK, 1); t.px(15, 16, INK, 1);
  // A floating question mark.
  t.hline(22, 3, 3, R.violet, 1); t.px(25, 4, R.violet, 1); t.px(24, 5, R.violet, 1); t.px(23, 6, R.violet, 1); t.px(23, 8, R.violet, 0);
};

const sphynxfi: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Lying pose: long body, front paws forward.
  t.ell(m(20), 22, 10, 5.5, R.sandstone);
  t.cap(m(27), 24, m(31), 27, 1.4, R.sandstone, { shade: 1 });
  for (const x of [5, 10]) t.cap(m(x + 4), 25.5, m(x), 27.5, 2.1, R.sandstone, { shade: 1 });
  // Violet wings folded on the back.
  t.poly([[m(14), 18], [m(28), 10], [m(27), 19]], R.violet, { grad: true });
  for (let k = 0; k < 3; k++) t.line(m(17 + k * 3), 17 - k, m(26), 12 + k * 2, R.violet, 3);
  headcloth(t, m(10), 5, 7.5, 13, R.gold, R.violet);
  t.ell(m(10), 12, 6.2, 5.6, R.sandstone);
  if (back) return;
  eyeGlow(t, 5, 10, R.neon);
  eyeGlow(t, 11, 10, R.neon);
  nose(t, 9, 14);
  t.hline(8, 16, 4, INK, 1);
  // Cobra-less crest: a floating black candle for a jewel.
  t.rect(9, 2, 2, 4, R.crow, 3);
  t.vline(10, 0, 2, R.ink, 1);
};

const wraplet: Paint = (t, back) => {
  paws(t, 16, 4, 2.2, R.wrap);
  t.ell(16, 21, 7, 6, R.wrap);
  t.ell(16, 12, 7, 6.2, R.wrap);
  // Bandage wraps.
  for (const [y, dx] of [[8, 0], [11, 1], [14, -1], [18, 1], [21, 0], [24, -1]] as const) t.line(9 + dx, y, 23 + dx, y + 1.5, R.wrap, 3);
  // A loose end fluttering.
  t.cap(22, 14, 28, 12, 1, R.wrap, { spec: false });
  if (back) return;
  // One eye peeks through the gap; a rug-purple glow inside.
  t.rect(10, 11, 12, 3, INK, 1);
  t.rect(11, 12, 10, 1, R.violet, 2);
  t.rect(17, 11, 3, 3, R.violet, 1);
  t.px(18, 11, WHITE);
};

const wrappharaoh: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Crook staff with a coin at the hook.
  t.vline(m(27), 8, 21, R.bronze, 1);
  t.cap(m(27), 8, m(29), 4, 1, R.bronze, { spec: false });
  coin(t, m(29), 4, 1.6);
  for (const x of [11, 20]) t.cap(x, 24, x, 28.5, 2.6, R.wrap, { shade: 1, spec: false });
  t.slab(15.5, 18.5, 8, 7.5, 3, R.wrap);
  t.cap(8, 13, m(4), 20, 2, R.wrap, { shade: 1 });
  t.cap(23, 13, m(26), 17, 2, R.wrap, { shade: 1 });
  for (const [y, dx] of [[13, 0], [17, 1], [21, -1], [24, 0]] as const) t.line(8 + dx, y, 23 + dx, y + 1.2, R.wrap, 3);
  // Royal collar and a striped headcloth.
  t.ell(15.5, 12, 8, 2.6, R.gold, { rim: false });
  for (const x of [10, 13, 16, 19, 22]) t.px(x, 12, R.violet, 1);
  headcloth(t, 15.5, 1, 7.5, 12, R.gold, R.violet);
  t.ell(15.5, 7, 5.5, 5, R.wrap);
  if (back) return;
  t.rect(11, 6, 10, 3, INK, 1);
  eyeGlow(t, 11, 5, R.violet);
  eyeGlow(t, 17, 5, R.violet);
  t.line(12, 9, 19, 10, R.wrap, 3);
  // Cobra-less uraeus: a small rug-purple gem on the brow.
  t.ell(15.5, 1.5, 1.4, 1.4, R.violet, { spec: false });
};

// ------------------------------------------------------------------------------------------------
// Legends
// ------------------------------------------------------------------------------------------------

const arborealm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Four slender legs and a deer body.
  for (const x of [9, 13, 20, 24]) {
    t.cap(x, 21, x, 28, 1.4, R.brown, { shade: x > 16 ? 1 : 0, spec: false });
    t.ell(x, 28.6, 1.6, 0.9, R.bark, { spec: false });
  }
  t.ell(16.5, 19.5, 10, 5, R.brown);
  t.ell(m(26.5), 17, 2, 2, R.cream, { spec: false });
  // Neck and head.
  t.cap(m(10), 18, m(9), 10, 2.8, R.brown);
  t.ell(m(8.5), 9, 3.8, 3.4, R.brown);
  // Blossom-tree antlers: two branching crowns heavy with flowers.
  for (const s of [-1, 1] as const) {
    const bx = m(8.5) + s * 2;
    t.line(bx, 6.5, bx + s * 6, 1, R.bark, 1);
    t.line(bx + s * 2, 4.5, bx + s * 1.5, 0, R.bark, 1);
    t.line(bx + s * 4, 3, bx + s * 7.5, 3.5, R.bark, 1);
    t.ell(bx + s * 6, 1, 3.4, 2.2, R.pink, { rim: false });
    t.ell(bx + s * 1.5, 0.5, 2.4, 1.6, R.pink, { rim: false });
    t.ell(bx + s * 7.5, 4, 2.2, 1.6, R.pink, { rim: false });
    t.px(Math.round(bx + s * 6), 0, WHITE);
  }
  // Glowing leaf markings along the back.
  for (const x of [13, 17, 21]) leaf(t, m(x), 16, m(x + 2), 14.5, 0.9, R.lime);
  if (back) return;
  // Crescent on the brow, calm eyes.
  t.ell(8.5, 6.5, 1.4, 1.2, R.moon, { spec: false });
  t.px(9, 6, R.brown, 2);
  t.rect(6, 8, 2, 2, INK, 1); t.px(6, 8, WHITE);
  t.rect(10, 8, 2, 2, INK, 1); t.px(10, 8, WHITE);
  t.px(8, 11, INK, 1);
};

const rektquiem: Paint = (t, back) => {
  // Tattered spectral cloak.
  t.poly([[5, 12], [27, 12], [30, 29], [25, 26], [21, 30], [16, 26], [11, 30], [7, 26], [2, 29]], R.crow, { grad: true });
  t.ell(16, 16, 8, 7, R.wisp, { dome: 0.5 });
  // Floating hands, one playing a ghostly organ chord.
  t.ell(4, 18, 2.2, 2, R.wisp, { spec: false });
  t.ell(28, 17, 2.2, 2, R.wisp, { spec: false });
  for (const [x, y] of [[1, 10], [3, 7], [29, 9], [31, 12]] as const) t.px(x, y, R.jade, 1);
  // Crown of black candles with pale flames.
  t.rect(9, 5, 14, 3, R.gold, 1);
  for (const x of [10, 14, 18, 22]) {
    t.rect(x - 1, 1, 2, 4, R.crow, 3);
    t.px(x - 1, 0, R.jade, 0);
  }
  if (back) {
    t.hline(9, 12, 14, R.crow, 3);
    return;
  }
  // Skull-pale face with hollow glowing eyes.
  t.ell(16, 12, 5.5, 4.5, R.wisp, { dome: 0.4 });
  t.ell(13.5, 11.5, 1.6, 1.8, R.ink, { flat: 1, occ: false });
  t.ell(18.5, 11.5, 1.6, 1.8, R.ink, { flat: 1, occ: false });
  t.px(13, 11, R.jade, 0); t.px(18, 11, R.jade, 0);
  teeth(t, 13, 15, 7);
  // A red candle chart falling across its chest.
  for (const [x, y, h] of [[11, 18, 3], [14, 19, 4], [17, 21, 3], [20, 22, 4]] as const) t.vline(x, y, h, R.red, 1);
};

export const SIDECHAIN_PAINT: Partial<Record<SidechainId, Paint>> = {
  pumpkoin, gourdian, crowlet, crowllateral, nutling, nutvault,
  rektling, rektoplasm, tombstonk, cryptkeep, webthree, arachnft, deadcat, catacomb,
  toucoin, beakfolio, vinesnek, vineconda, slothlet, slothstake,
  koinfish, ryukoin, origamint, craneprint, sproutle, bonsaitoise,
  golemint, colosstake, riddlekit, sphynxfi, wraplet, wrappharaoh,
  arborealm, rektquiem,
};

void eyeDot;
